/**
 * Rate Guard — prevents runaway infinite loops by tracking call frequency per key.
 *
 * Two layers of protection:
 *
 * 1. **Rapid-fire detection** — Tracks the time gap between consecutive calls
 *    to the same key. If 5+ calls arrive back-to-back with < 50ms between each,
 *    it's blocked as a loop. No human or network response can trigger calls that
 *    fast — only a re-render / useEffect loop can. Catches loops within ~250ms.
 *
 * 2. **Sliding window limit** — If the same key fires more than MAX_CALLS within
 *    a 60-second window, subsequent calls are blocked. Catches slower loops.
 *
 * Every blockage is logged to Firestore (`errorLogs` collection) for monitoring.
 * Uses raw Firebase `setDoc` (bypasses the rate-guarded wrapper) and is
 * fire-and-forget so it never blocks the error throw.
 *
 * Usage:
 *   enforceRateLimit('getDocs:users/abc/extensions/app/uaOrgUsers');
 *   // throws if rapid-fire detected or exceeds 30 calls/min
 */

import { doc, setDoc as _rawSetDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { auth } from '@/config/firebase';
import { toast } from 'sonner';

// Throttle rate-limit toasts so a burst of blocked calls doesn't spam the user
let lastRateLimitToastAt = 0;
function showRateLimitToast(): void {
  const now = Date.now();
  if (now - lastRateLimitToastAt < 5000) return;
  lastRateLimitToastAt = now;
  toast.error('Rate limited to your account, Try again');
}

const DEFAULT_MAX_CALLS = 45;
const WINDOW_MS = 60_000; // 1 minute

/**
 * Bulk-mode reference counter. While > 0, `enforceRateLimit` is a no-op —
 * neither the rapid-fire detector (Layer 1) nor the sliding-window cap
 * (Layer 2) blocks the call. Bulk sends fire hundreds of requests
 * intentionally; loop-detection heuristics designed for runaway useEffect
 * cycles produce false positives there.
 *
 * Use the depth counter (not a boolean) so concurrent / nested bulk
 * operations compose. Always pair `enterBulkMode()` with the returned
 * `release()` in a `finally` so an exception mid-batch doesn't leave the
 * guard permanently disabled.
 *
 * Lives in `rate-guard.ts` (rather than `proxy.ts`) so EVERY caller of
 * `enforceRateLimit` — proxy CF requests, Firestore reads/writes — gets
 * the bypass for free during a batch. Otherwise a bulk send that also
 * touches Firestore (sender doc, templates, history logging) would still
 * hit the 45/min Firestore cap.
 */
let _bulkOperationDepth = 0;

export function enterBulkMode(): () => void {
  _bulkOperationDepth++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    _bulkOperationDepth = Math.max(0, _bulkOperationDepth - 1);
  };
}

/** True when at least one bulk operation is currently in flight. */
export function isBulkModeActive(): boolean {
  return _bulkOperationDepth > 0;
}

// ── Layer 1: Rapid-fire detection ──
const RAPID_GAP_MS = 50;       // calls closer than 50ms apart = suspicious
const RAPID_STREAK_LIMIT = 5;  // 5 consecutive rapid calls = loop → block
const RAPID_STREAK_WARN = 3;   // warn at 3 consecutive rapid calls

// Per-key tracking: last call timestamp + consecutive rapid-call streak
const rapidLog = new Map<string, { lastCall: number; streak: number }>();

// Map of key → array of timestamps (sorted ascending) for sliding window
const callLog = new Map<string, number[]>();

// Track which keys have already shown warnings (avoid spam)
const warned = new Set<string>();
const rapidWarned = new Set<string>();

// Prevent duplicate Firestore logs for the same key within a short window
const loggedKeys = new Set<string>();
const LOG_DEDUP_MS = 60_000; // one log per key per minute

/**
 * Log a rate-guard blockage to Firestore for monitoring.
 * Uses raw Firebase setDoc — bypasses the rate-guarded wrapper to avoid recursion.
 * Fire-and-forget: never blocks, never throws.
 */
function logBlockageToFirestore(
  type: 'rapid-fire' | 'sliding-window',
  key: string,
  displayLabel: string,
  details: Record<string, unknown>
): void {
  // Deduplicate — don't spam Firestore with the same key
  const dedupeKey = `${type}:${key}`;
  if (loggedKeys.has(dedupeKey)) return;
  loggedKeys.add(dedupeKey);
  setTimeout(() => loggedKeys.delete(dedupeKey), LOG_DEDUP_MS);

  try {
    const uid = auth.currentUser?.uid ?? 'anonymous';
    const timestamp = new Date().toISOString();
    const docId = `rateguard_${uid}_${Date.now()}`;
    const ref = doc(db, 'appCrashReports', docId);

    _rawSetDoc(ref, {
      source: 'rate-guard',
      type,
      key,
      label: displayLabel,
      uid,
      userEmail: auth.currentUser?.email ?? null,
      timestamp,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...details,
    }).catch(() => {
      // silently ignore — logging should never break the app
    });
  } catch {
    // silently ignore — if Firebase isn't ready, skip logging
  }
}

/**
 * Check rate limit and throw if exceeded.
 *
 * @param key  Unique identifier for the request (e.g. `getDocs:path` or `CF:url`)
 * @param label  Human-readable label for the log message
 * @param maxCalls  Override the default limit (default: 45 for Firestore, pass 300 for CF)
 * @throws Error if the rate limit is exceeded
 */
export function enforceRateLimit(key: string, label?: string, maxCalls = DEFAULT_MAX_CALLS): void {
  // Bulk-mode bypass — see `enterBulkMode` above. The user has explicitly
  // initiated a batch operation, so neither loop-detection nor throughput
  // caps should apply. Returns before touching any tracking state so a
  // 1000-recipient send doesn't pollute the streak/window maps with
  // entries that would falsely flag follow-up calls after the batch ends.
  if (_bulkOperationDepth > 0) return;

  const now = Date.now();
  const displayLabel = label ?? key;

  // ── Layer 1: Rapid-fire detection ──
  let rapid = rapidLog.get(key);
  if (!rapid) {
    rapid = { lastCall: 0, streak: 0 };
    rapidLog.set(key, rapid);
  }

  const gap = now - rapid.lastCall;
  rapid.lastCall = now;

  if (gap < RAPID_GAP_MS) {
    rapid.streak++;
  } else {
    rapid.streak = 0; // gap was normal → reset streak
  }

  // Blocked — rapid-fire loop detected
  if (rapid.streak >= RAPID_STREAK_LIMIT) {
    const message = `[Rate Guard] LOOP BLOCKED: "${displayLabel}" fired ${rapid.streak} times in < ${RAPID_GAP_MS}ms intervals — useEffect or re-render loop detected`;
    if (import.meta.env.DEV) {
      console.error(message, '\n📍 Call stack:', new Error().stack);
    } else {
      console.error(message);
    }
    logBlockageToFirestore('rapid-fire', key, displayLabel, {
      streak: rapid.streak,
      gapMs: gap,
    });
    showRateLimitToast();
    rapid.streak = 0; // reset so it can detect again after recovery
    throw new Error(message);
  }

  // Early warning for rapid-fire
  if (rapid.streak >= RAPID_STREAK_WARN && !rapidWarned.has(key)) {
    rapidWarned.add(key);
    const msg = `[Rate Guard] LOOP WARNING: "${displayLabel}" fired ${rapid.streak} times in < ${RAPID_GAP_MS}ms intervals — possible loop`;
    if (import.meta.env.DEV) {
      console.warn(msg, '\n📍 Call stack:', new Error().stack);
    } else {
      console.warn(msg);
    }
    setTimeout(() => rapidWarned.delete(key), 2_000);
  }

  // ── Layer 2: Sliding window limit ──
  let timestamps = callLog.get(key);
  if (!timestamps) {
    timestamps = [];
    callLog.set(key, timestamps);
  }

  // Evict timestamps outside the sliding window
  const cutoff = now - WINDOW_MS;
  while (timestamps.length > 0 && timestamps[0] < cutoff) {
    timestamps.shift();
  }

  const count = timestamps.length;
  const warnAt = Math.floor(maxCalls * 2 / 3);

  // Blocked — exceeded sliding window limit
  if (count >= maxCalls) {
    const message = `[Rate Guard] BLOCKED: "${displayLabel}" exceeded ${maxCalls} calls/min — possible infinite loop`;
    if (import.meta.env.DEV) {
      console.error(message, '\n📍 Call stack:', new Error().stack);
    } else {
      console.error(message);
    }
    logBlockageToFirestore('sliding-window', key, displayLabel, {
      callCount: count,
      maxCalls,
      windowMs: WINDOW_MS,
    });
    showRateLimitToast();
    throw new Error(message);
  }

  // Early warning for sliding window
  if (count >= warnAt && !warned.has(key)) {
    warned.add(key);
    const msg = `[Rate Guard] WARNING: "${displayLabel}" has fired ${count} times in the last 60s`;
    if (import.meta.env.DEV) {
      console.warn(msg, '\n📍 Call stack:', new Error().stack);
    } else {
      console.warn(msg);
    }
    setTimeout(() => warned.delete(key), WINDOW_MS);
  }

  // Record this call
  timestamps.push(now);
}
